/**
 * Seiteneinrichtung über die Ränder hinaus: Papierformat, Textspalten,
 * Zeilennummern, „Erste Seite anders“, Seitenrahmen und Silbentrennung.
 *
 * Alle Angaben gehören zu WordApiDesktop 1.3 und stehen damit nur in Word für
 * Windows und Mac zur Verfügung. `readSeite()` liefert sonst `null`; die
 * Aufgaben weichen dann auf das Prüfmakro „Dokument auslesen“ aus, das
 * dieselben Angaben über VBA ermittelt.
 */

import { supportsWordApiDesktop, wordApiDesktop } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface AbschnittSeite {
  index: number;
  /** z. B. "A4", "A5", "Letter" oder "Custom". */
  paperSize: string | null;
  /** Seitenmaße in Punkt. */
  pageWidth: number | null;
  pageHeight: number | null;
  /** Zahl der Textspalten, 1 = keine Spalten. */
  textColumns: number;
  lineNumbering: boolean;
  differentFirstPage: boolean;
  /** Seitenrahmen („Entwurf → Seitenränder“). */
  seitenrahmen: boolean;
}

export interface SeitenSnapshot {
  abschnitte: AbschnittSeite[];
  silbentrennung: boolean | null;
}

function zahl(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export async function readSeite(): Promise<SeitenSnapshot | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop(wordApiDesktop.seitenDetails)) return null;

  try {
    return await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      context.document.load("autoHyphenation");
      await context.sync();

      const anfragen = sections.items.map((section) => {
        const pageSetup = section.pageSetup;
        pageSetup.load("paperSize,pageWidth,pageHeight,differentFirstPageHeaderFooter");
        const spalten = pageSetup.textColumns;
        spalten.load("items");
        const zeilen = pageSetup.lineNumbering;
        zeilen.load("isActive");
        const rahmen = section.borders;
        rahmen.load("items/isVisible,items/lineStyle");
        return { pageSetup, spalten, zeilen, rahmen };
      });
      await context.sync();

      return {
        silbentrennung:
          typeof context.document.autoHyphenation === "boolean"
            ? context.document.autoHyphenation
            : null,
        abschnitte: anfragen.map(({ pageSetup, spalten, zeilen, rahmen }, index) => ({
          index,
          paperSize: typeof pageSetup.paperSize === "string" ? pageSetup.paperSize : null,
          pageWidth: zahl(pageSetup.pageWidth),
          pageHeight: zahl(pageSetup.pageHeight),
          textColumns: Math.max(1, spalten.items.length),
          lineNumbering: zeilen.isActive === true,
          differentFirstPage: pageSetup.differentFirstPageHeaderFooter === true,
          seitenrahmen: rahmen.items.some(
            (border) => border.isVisible === true && border.lineStyle !== "None",
          ),
        })),
      };
    });
  } catch {
    return null;
  }
}

/** Seitenformat aus den Maßen bestimmen – unabhängig vom gemeldeten Namen. */
export function formatAusMassen(breite: number | null, hoehe: number | null): string | null {
  if (breite === null || hoehe === null) return null;
  const kurz = Math.min(breite, hoehe);
  const lang = Math.max(breite, hoehe);
  const passt = (b: number, h: number) => Math.abs(kurz - b) < 4 && Math.abs(lang - h) < 4;
  if (passt(595.3, 841.9)) return "A4";
  if (passt(419.55, 595.3)) return "A5";
  if (passt(841.9, 1190.55)) return "A3";
  if (passt(612, 792)) return "Letter";
  if (passt(612, 1008)) return "Legal";
  return null;
}
